import { useMemo } from "react";
import { useStore } from "hooks/use-store";
import {
  convertUnixMicrosecondsToDate,
  formatDegreesToCompass,
} from "utils/converters";
import { formatPercentage, roundNumber } from "utils/formatters";
import { PLACEHOLDER } from "utils/constants";

const FORECAST_AGE_LIMIT = 60 * 60 * 1000; // 1 hour in milliseconds

export const useWeather = () => {
  const data = useStore((state) => state.weatherData);

  const forecastTime = convertUnixMicrosecondsToDate(data?.time ?? null);
  const fetchTime = convertUnixMicrosecondsToDate(data?.timestamp ?? null);

  const isStale = useMemo(() => {
    if (!fetchTime || !forecastTime) return true;
    const currentTime = new Date();
    const forecastAge = currentTime.getTime() - forecastTime.getTime();
    return forecastAge > FORECAST_AGE_LIMIT;
  }, [fetchTime, forecastTime]);

  const formattedData = useMemo(() => {
    // if no data is available, return default values
    if (!data) {
      return Object.keys(weatherAdapter).reduce((acc, key) => {
        const adapter = weatherAdapter[key];
        acc[key] = {
          value: PLACEHOLDER,
          unit: adapter ? adapter.unit : null,
        };
        return acc;
      }, {} as Record<string, { value: string | number; unit: string | null }>);
    }

    return Object.entries(data).reduce((acc, [key, value]) => {
      const adapter = weatherAdapter[key];
      if (adapter) {
        acc[key] = {
          value: isStale ? PLACEHOLDER : adapter.format(value),
          unit: adapter.unit,
        };
      } else {
        acc[key] = { value: isStale ? PLACEHOLDER : value, unit: null }; // Fallback for unhandled keys
      }
      return acc;
    }, {} as Record<string, { value: string | number; unit: string | null }>);
  }, [data, isStale]);

  return {
    data: formattedData,
    forecastTime,
    fetchTime,
    isStale,
  };
};

const weatherAdapter = {
  time: {
    format: convertUnixMicrosecondsToDate,
    unit: null,
  },
  timestamp: {
    format: convertUnixMicrosecondsToDate,
    unit: null,
  },
  air_temperature: {
    format: (value: string) => roundNumber(value, 1),
    unit: "°C",
  },
  cloud_cover: {
    format: formatPercentage,
    unit: "%",
  },
  current_direction: {
    format: (value: string) => roundNumber(value, 0),
    unit: "°",
  },
  current_speed: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m/s",
  },
  gust: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m/s",
  },
  humidity: {
    format: formatPercentage,
    unit: "%",
  },
  ice_cover: {
    format: formatPercentage,
    unit: "%",
  },
  precipitation: {
    format: (value: string) => roundNumber(value, 1),
    unit: "mm/h",
  },
  pressure: {
    format: (value: string) => roundNumber(value, 0),
    unit: "hPa",
  },
  sea_level: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m",
  },
  sea_state: {
    format: (value: string) => roundNumber(value, 0),
    unit: "Beaufort scale 0-12",
  },
  sea_state_wave: {
    format: (value: string) => roundNumber(value, 0),
    unit: "Douglas scale 0-9",
  },
  swell_direction: {
    format: (value: string) => roundNumber(value, 0),
    unit: "°",
  },
  swell_height: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m",
  },
  swell_period: {
    format: (value: string) => roundNumber(value, 1),
    unit: "s",
  },
  visibility: {
    format: formatPercentage,
    unit: "%",
  },
  water_temperature: {
    format: (value: string) => roundNumber(value, 1),
    unit: "°C",
  },
  wave_direction: {
    format: (value: string) => roundNumber(value, 0),
    unit: "°",
  },
  wave_height: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m",
  },
  wave_period: {
    format: (value: string) => roundNumber(value, 1),
    unit: "s",
  },
  wind_direction: {
    format: formatDegreesToCompass,
    unit: "°",
  },
  wind_speed: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m/s",
  },
  wind_wave_direction: {
    format: (value: string) => roundNumber(value, 0),
    unit: "°",
  },
  wind_wave_height: {
    format: (value: string) => roundNumber(value, 1),
    unit: "m",
  },
  wind_wave_period: {
    format: (value: string) => roundNumber(value, 1),
    unit: "s",
  },
};
